import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SearchComponent = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Responsive inline styles
  const styles = {
    container: {
      position: 'relative',
      width: '100%', // Full width on mobile
      maxWidth: '250px', // Maximum width on larger screens
      marginLeft: 'auto',
      padding: '0 10px', // Add some padding for smaller screens
      boxSizing: 'border-box', // Ensure padding doesn't affect width
    },
    inputWrapper: {
      position: 'relative',
      width: '100%',
    },
    input: {
      width: '100%',
      padding: '8px 30px 8px 10px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
      boxSizing: 'border-box', // Ensure padding doesn't affect width
    },
    searchIcon: {
      position: 'absolute',
      right: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#888',
      cursor: 'pointer',
      display: 'flex', // Better touch target for mobile
      alignItems: 'center',
      justifyContent: 'center',
      width: '30px', // Wider touch area
      height: '30px',
    },
    suggestionsDropdown: {
      position: 'absolute',
      top: '100%',
      left: '0',
      width: '100%',
      maxHeight: '200px',
      overflowY: 'auto',
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderTop: 'none',
      borderRadius: '0 0 4px 4px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      zIndex: 10,
    },
    suggestionItem: {
      padding: '12px 10px', // Increased padding for better touch targets
      cursor: 'pointer',
      fontSize: '14px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap', // Allow wrapping on small screens
    },
    suggestionItemHover: {
      backgroundColor: '#f0f0f0',
    },
    suggestionText: {
      flex: '1 1 auto', // Allow text to shrink but prefer growing
      minWidth: '60%', // Ensure text gets priority
      wordBreak: 'break-word', // Break long words if needed
    },
    suggestionType: {
      fontSize: '12px',
      color: '#888',
      marginLeft: '10px',
      flex: '0 0 auto', // Don't allow shrinking
    },
  };

  useEffect(() => {
    // Function to handle clicks outside the suggestions dropdown
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (query.trim() === '') {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetch(`https://flask.finstrats.com/search?key=${encodeURIComponent(query)}`);
        
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        
        const data = await response.json();
        setSuggestions(data || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce function to avoid making too many requests
    const timeoutId = setTimeout(() => {
      if (query.trim() !== '') {
        fetchSuggestions();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleQueryChange = (e) => {
    setQuery(e.target.value);
    if (e.target.value.trim() === '') {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Determine navigation based on suggestion type
    let navigationPath = '';
    let displayText = '';

    // Handle different suggestion formats
    if (suggestion.company_name && suggestion.ticker_code) {
      // Stock suggestion
      navigationPath = `/stockdetails?ticker=${suggestion.ticker_code}`;
      displayText = `${suggestion.ticker_code}`;
    } else if (suggestion.name && suggestion.type) {
      // Generic suggestion with name and type
      switch(suggestion.type.toLowerCase()) {
        case 'stock':
          navigationPath = `/stockdetails?ticker=${suggestion.name}`;
          break;
        case 'industry':
          navigationPath = `/industry?name=${suggestion.name}`;
          break;
        // Add more cases as needed
        default:
          navigationPath = `/search?q=${suggestion.name}`;
      }
      displayText = suggestion.name;
    } else if (typeof suggestion === 'string') {
      // Simple string suggestion
      navigationPath = `/search?q=${suggestion}`;
      displayText = suggestion;
    } else {
      // Fallback
      navigationPath = '/search';
      displayText = JSON.stringify(suggestion);
    }

    // Set query and navigate
    setQuery(displayText);
    setShowSuggestions(false);
    
    // Navigate to the determined path
    navigate(navigationPath);
  };

  const getSuggestionDisplayText = (suggestion) => {
    // Convert object to displayable text with type indication
    if (typeof suggestion === 'object') {
      if (suggestion.company_name && suggestion.ticker_code) {
        return (
          <div style={{...styles.suggestionItem, padding: '0'}}>
            <span style={styles.suggestionText}>{suggestion.company_name}</span>
            <span style={styles.suggestionType}>{suggestion.ticker_code}</span>
          </div>
        );
      }
      if (suggestion.name && suggestion.type) {
        return (
          <div style={{...styles.suggestionItem, padding: '0'}}>
            <span style={styles.suggestionText}>{suggestion.name}</span>
            <span style={styles.suggestionType}>{suggestion.type}</span>
          </div>
        );
      }
      return JSON.stringify(suggestion);
    }
    return suggestion;
  };

  const handleSearch = () => {
    // Global search navigation
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setShowSuggestions(false);
    }
  };

  // Media query styles handling
  const getResponsiveStyles = () => {
    const width = window.innerWidth;
    
    if (width <= 480) {
      // Mobile styles
      return {
        containerStyle: {
          ...styles.container,
          maxWidth: '100%',
          padding: '0 5px',
        },
        inputStyle: {
          ...styles.input,
          fontSize: '16px', // Larger font for mobile
          padding: '10px 35px 10px 10px', // Larger touch area
        },
        suggestionItemStyle: {
          ...styles.suggestionItem,
          padding: '14px 10px', // Even larger touch targets on mobile
        },
      };
    } else if (width <= 768) {
      // Tablet styles
      return {
        containerStyle: {
          ...styles.container,
          maxWidth: '90%',
        },
        inputStyle: {
          ...styles.input,
          fontSize: '15px',
        },
        suggestionItemStyle: {
          ...styles.suggestionItem,
          padding: '12px 10px',
        },
      };
    } else {
      // Desktop - default styles
      return {
        containerStyle: styles.container,
        inputStyle: styles.input,
        suggestionItemStyle: styles.suggestionItem,
      };
    }
  };

  // Get responsive styles based on window size
  const [responsiveStyles, setResponsiveStyles] = useState(getResponsiveStyles());

  // Update styles on window resize
  useEffect(() => {
    const handleResize = () => {
      setResponsiveStyles(getResponsiveStyles());
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div style={responsiveStyles.containerStyle}>
      <div style={styles.inputWrapper}>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleQueryChange}
          onFocus={() => query.trim() !== '' && setShowSuggestions(true)}
          placeholder="Search..."
          style={responsiveStyles.inputStyle}
        />
        <div 
          style={styles.searchIcon}
          onClick={handleSearch}
        >
          <Search size={18} />
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          style={styles.suggestionsDropdown}
        >
          {suggestions.map((suggestion, index) => (
            <div 
              key={index}
              style={responsiveStyles.suggestionItemStyle}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = styles.suggestionItemHover.backgroundColor}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              {getSuggestionDisplayText(suggestion)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchComponent;